import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {useTranslation} from "next-i18next";
import {useRouter} from "next/router";
import Container from "../../components/Container";
import Layout from "../../components/Layout";
import Meta from "../../components/Meta";
import SocialBlock from "../../components/SocialBlock";
import HowToBlog from "../../components/HowToBlog";
import AuthorBlock from "../../components/AuthorBlock";
import ArticleCard from "../../components/ArticleCard";
import Image from "next/image";
import LinkBlock from "../../components/LinksBlock";
import Script from "next/script";
import InfoBlockBlog from "../../components/InfoBlockBlog";
import {getTranslation} from "../../utils/getTranslation";
import generateBlogBreadcrumbList from "../../utils/generateBlogBreadcrumbList";

const PageBlog = () => {
  const {query, basePath, locales, locale: currentLocale} = useRouter();
  const title = query.title;
  const {t} = useTranslation(["common", "blog"]);
  const article = (t(`blog:articles`, {returnObjects: true}))[title];
  const articles = Object.values(t("blog:articles", {returnObjects: true}));
  const alternates = []
  for (const locale of locales) {
    const translation = getTranslation(locale, 'articles')
    const currentTranslation = getTranslation(currentLocale, 'articles')
    const englishTitle = currentTranslation[title] && currentTranslation[title].englishTitle;
    const [key] = translation && Object.entries(translation).find(([, article]) => article.englishTitle === englishTitle);
    alternates.push({
      href: locale === "en" ? `/blog/${key}` : `/${locale}/blog/${key}`,
      hreflang: locale,
    })
  }

  const jsonLdData = {
    "@context": "http://schema.org",
    "@type": "BlogPosting",
    "isPartOf":
      {
        "@type": "Blog",
        "name": "PDFEdit Blog",
        "url": "https://pdfedit.pro/blog",
        "inLanguage": currentLocale,
        "publisher":
          {
            "@type": "Organization",
            "name": "PDFEdit.pro",
            "url": "https://pdfedit.pro/",
          },
      },
    "mainEntityOfPage": `https://pdfedit.pro/${currentLocale === 'en' ? '' : `${currentLocale}/`}blog/${article.link}`,
    "headline": article.meta.title,
    "author":
      {
        "@type": "Person",
        "name": article.author,
        "jobTitle": article.authorWork,
        "image":
          {
            "@type": "imageObject",
            "encodingFormat": "image/jpeg",
            "url": article.authorImageURL,
            "width": 48,
            "height": 48,
          },
      },
    "datePublished": article.datetime,
    "dateModified": article.datetime,
    "image":
      [
        {
          "@type": "imageObject",
          "encodingFormat": "image/jpeg",
          "url": `https://pdfedit.pro/images/${article.articleImage}`,
          "width": 815,
          "height": 320,
        },
      ],
    "publisher": {
      "@type": "Organization",
      "name": "PDFEdit.pro",
      "url": "https://pdfedit.pro/",
      "logo":
        {
          "@type": "imageObject",
          "url": "https://pdfedit.pro/images/logo.svg",
        },
    },
  };
  const breadcrumbJson = generateBlogBreadcrumbList(basePath, article);

  return (
    <Layout>
      <Meta
        author={article.author}
        title={article.meta.title}
        publishedTime={article.date}
        description={article.meta.description}
        alternates={[{href: `/blog/${article.englishTitle}`, hreflang: "x-default"}, ...alternates]}
        canonical={currentLocale === "en" ? `/blog/${title}` : `/${currentLocale}/blog/${title}`}
        ogType="article"
        ogTitle={article.meta.title}
        image={article.articleImage ? `${basePath}/images/${article.articleImage}` : null}
        ogUrl={currentLocale === "en" ? `/blog/${title}` : `/${currentLocale}/blog/${title}`}
        ogSiteName="PDFEdit.pro"
        twitterCard="summary_large_image"
        twitterSite="@Smallpdf"
      />
      <Script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLdData)}}/>
      <Script type="application/ld+json" dangerouslySetInnerHTML={{__html: breadcrumbJson}}/>
      <Container className="page-blog-background">
        <LinkBlock links={[{text: "Home", link: "/"}, {text: "Blog", link: "/blog"}, {
          text: article.title,
          link: `/blog/${title}`,
        }]}/>
        <div className="page-blog__top-block">
          <h1 className="page-blog__title">{article.title}</h1>
          <h3 className="page-blog__description">{article.description}</h3>
        </div>
        <div className="page-blog__content">
          <Image className="page-blog__image" src={`${basePath}/images/${article.articleImage}`} width={815}
                 height={320} alt=""/>
          <div className="page-blog__content">
            <InfoBlockBlog date={article.date} image={article.authorImage} author={article.author}/>
            <div className="page-blog__block">
              {article.text.map((item, index) => <p className="page-blog__text" key={index}>{item}</p>)}
            </div>
            {article.contents.map(content => <HowToBlog key={content.title} title={content.title}
                                                        content={content.content}/>)}
          </div>
          <SocialBlock isBottom={true}/>
          <AuthorBlock image={article.authorImage} work={article.authorWork} name={article.author}/>
        </div>
      </Container>
      <Container>
        <div className="blog__articles-block">
          {articles.map((item) =>
            item.title !== article.title &&
            <ArticleCard key={item.title} text={item.text[0]} title={item.title} date={item.date}
                         image={item.articleImage} link={item.link} readMore={t("blog:readMore")}/>,
          )}
        </div>
      </Container>
    </Layout>
  );
};

export const getServerSideProps = async ({locale}) => ({
  props: {
    ...(await serverSideTranslations(locale, ["common", "index", "blog"])),
  },
});

export default PageBlog;
